import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Package, CheckCircle2, Truck, RotateCcw,
  ArrowRight, Clock, Search, Home, Tag, ChevronRight, User,
  Plus, MapPin, X, Building2, Hash, Globe2, Copy, Check, Ban,
  MessageSquare, Phone,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../../redux/store";
import {
  listenToStoreHostOrders,
  cancelStoreOrder,
  refundStoreOrder,
  StoreOrder,
  markStoreItemsDelivered,
} from "../../../redux/storeOrdersSlice";
import { fetchAllUsers } from "../../../redux/authSlice";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
} from "../../../redux/storeProductSlice";

/* ═══════════════════════════════════════════════
   HEARTLAS TOKENS — crimson, espresso, cream.
   No gold anywhere: the accent ladder is red →
   redDark, with green reserved for money earned.
═══════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",
  blush: "#FDEEF0",
  border: "#EEDFC4",
  divider: "#F3E6CE",
  green: "#1F7A45",
  greenSoft: "#E8F4EC",
};

const GRAD_RED = `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`;
/* gold-free sweep: espresso → crimson → espresso */
const SWEEP = `linear-gradient(90deg, ${HEART.dark}, ${HEART.red}, ${HEART.redDark}, ${HEART.red}, ${HEART.dark})`;

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:99px;}
  input,button{font-family:inherit;cursor:pointer;}
  input::placeholder{color:${HEART.textFaint};}
  :focus-visible{outline:2px solid ${HEART.red};outline-offset:3px;border-radius:8px;}

  @keyframes hcSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes hcRise{from{opacity:0;transform:translateY(14px) scale(.98)}to{opacity:1;transform:none}}
  @keyframes hcBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
  @keyframes hcWiggle{0%,100%{transform:rotate(-3deg)}50%{transform:rotate(3deg)}}

  /* ── 3D slab order card ── */
  .oc{
    background:${HEART.panelBg};border:3px solid ${HEART.dark};border-radius:24px;overflow:hidden;
    box-shadow:0 7px 0 ${HEART.dark},0 16px 30px rgba(27,14,6,.14);
    transition:transform .16s ease,box-shadow .16s ease;
    animation:hcRise .38s ease both;margin-bottom:18px;
  }
  .oc:hover{transform:translateY(-4px);box-shadow:0 11px 0 ${HEART.dark},0 22px 38px rgba(27,14,6,.18);}
  .oc.open{box-shadow:0 7px 0 ${HEART.red},0 18px 34px rgba(200,32,46,.24);}
  .oc-head{display:flex;align-items:stretch;cursor:pointer;background:${HEART.panelBg};}
  .oc-head:active{transform:translateY(2px);}

  /* ── domed item tile ── */
  .it{
    border:2.5px solid ${HEART.dark};border-radius:18px;overflow:hidden;background:${HEART.panelBg};
    box-shadow:0 4px 0 ${HEART.dark};transition:transform .16s ease,box-shadow .16s ease;
    display:flex;flex-direction:column;
  }
  .it:hover{transform:translateY(-3px);box-shadow:0 7px 0 ${HEART.dark},0 12px 20px rgba(27,14,6,.16);}
  .it:active{transform:translateY(3px);box-shadow:0 1px 0 ${HEART.dark};}
  .it-media{position:relative;width:100%;aspect-ratio:1/1;background:${HEART.blush};cursor:pointer;}
  .it-dome-tl{
    position:absolute;top:0;left:0;padding:6px 12px;border-bottom-right-radius:26px;z-index:3;
    background:linear-gradient(135deg,rgba(27,14,6,.9),${HEART.red});
    color:#fff;font-size:10.5px;font-weight:800;
    max-width:88%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .it-dome-br{
    position:absolute;bottom:0;right:0;padding:7px 13px;border-top-left-radius:26px;z-index:3;
    background:linear-gradient(135deg,#4A2018,${HEART.dark} 50%,${HEART.redDark});
    color:#fff;font-size:12.5px;font-weight:900;white-space:nowrap;
    font-family:'Sora',sans-serif;letter-spacing:-.3px;
  }
  .it-pill{
    position:absolute;bottom:7px;left:7px;padding:4px 10px;border-radius:999px;z-index:3;
    background:${HEART.redDark};color:#fff;font-size:10px;font-weight:800;
    max-width:70%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .it-foot{padding:9px 11px 11px;border-top:2.5px solid ${HEART.dark};background:${HEART.bg};}

  /* ── buttons ── */
  .btn{
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:8px 15px;border-radius:999px;font-size:12px;font-weight:800;
    font-family:'Sora',sans-serif;white-space:nowrap;
    border:2.5px solid ${HEART.dark};background:${HEART.panelBg};color:${HEART.dark};
    box-shadow:0 4px 0 ${HEART.dark};transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .btn:hover{transform:translateY(-2px);box-shadow:0 6px 0 ${HEART.dark};}
  .btn:active{transform:translateY(3px);box-shadow:0 1px 0 ${HEART.dark};}
  .btn.gr{background:${GRAD_RED};color:#fff;}
  .btn.bl{background:${HEART.blush};color:${HEART.dark};}
  .btn.gn{background:${HEART.greenSoft};color:${HEART.green};}
  .btn.rd{background:${HEART.redSoft};color:${HEART.redDark};}
  .btn.sm{padding:5px 11px;font-size:10.5px;border-width:2px;box-shadow:0 3px 0 ${HEART.dark};}
  .btn.sm:hover{box-shadow:0 5px 0 ${HEART.dark};}
  .btn.sm:active{box-shadow:0 1px 0 ${HEART.dark};}

  /* ── slim inline filter pills ── */
  .fc{
    display:inline-flex;align-items:center;gap:6px;flex-shrink:0;
    padding:7px 13px;border-radius:999px;border:2px solid ${HEART.dark};
    background:${HEART.panelBg};color:${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    font-family:'Sora',sans-serif;font-size:11.5px;font-weight:800;
    transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .fc:hover{transform:translateY(-2px);box-shadow:0 5px 0 ${HEART.dark};background:${HEART.blush};}
  .fc:active{transform:translateY(2px);box-shadow:0 1px 0 ${HEART.dark};}
  .fc.on{background:${GRAD_RED};color:#fff;border-color:${HEART.dark};}
  .fc-n{
    display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;
    padding:0 5px;border-radius:999px;font-size:10.5px;font-weight:900;
    background:${HEART.blush};color:${HEART.dark};
  }
  .fc.on .fc-n{background:rgba(255,255,255,.92);color:${HEART.redDark};}

  /* ── search ── */
  .sw{
    display:flex;align-items:center;flex:1;background:${HEART.panelBg};border-radius:14px;
    overflow:hidden;border:2px solid ${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    transition:box-shadow .16s ease;
  }
  .sw:focus-within{box-shadow:0 3px 0 ${HEART.red};}

  .tab{
    display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:800;
    color:${HEART.textFaint};padding:4px 10px;border:none;background:none;transition:color .18s;
    font-family:'DM Sans',sans-serif;
  }
  .tab.on{color:${HEART.red};}

  /* ── address sheet ── */
  .addr-overlay{
    position:fixed;inset:0;background:rgba(27,14,6,.5);backdrop-filter:blur(4px);z-index:200;
    display:flex;align-items:flex-end;justify-content:center;
  }
  @media(min-width:768px){.addr-overlay{align-items:center;}}
  .addr-sheet{
    background:${HEART.panelBg};width:100%;max-width:440px;
    border:3px solid ${HEART.dark};border-bottom:none;border-radius:24px 24px 0 0;
    max-height:85vh;overflow-y:auto;position:relative;
  }
  @media(min-width:768px){
    .addr-sheet{border-radius:24px;border-bottom:3px solid ${HEART.dark};margin:0 16px;
      box-shadow:0 8px 0 ${HEART.dark},0 30px 60px rgba(27,14,6,.3);}
  }
  .addr-row{display:flex;gap:12px;align-items:flex-start;padding:11px 0;border-bottom:1px solid ${HEART.divider};}
  .addr-row:last-child{border-bottom:none;}
  .addr-copy{
    display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:10px;
    flex-shrink:0;background:${HEART.panelBg};border:2px solid ${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    transition:transform .14s ease,box-shadow .14s ease;
  }
  .addr-copy:hover{transform:translateY(-2px);box-shadow:0 5px 0 ${HEART.dark};}
  .addr-copy:active{transform:translateY(2px);box-shadow:0 1px 0 ${HEART.dark};}

  @media (prefers-reduced-motion: reduce){
    .oc,.it,.btn,.fc{animation:none!important;transition:none!important;}
  }
  @media (max-width: 520px){ .items-grid{grid-template-columns:1fr 1fr!important;} }
`;

/* ─── helpers ─── */
const fmt = (n?: number) => (!n || isNaN(n) ? "0.00" : Number(n).toFixed(2));
const fmtD = (ts: any) => {
  if (!ts) return "—";
  const ms = ts?.seconds ? ts.seconds * 1000 : ts;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

type SK = "Pending" | "Processing" | "Delivered" | "Cancelled" | "Refunded";

const SCFG: Record<SK, { bg: string; tx: string; Icon: any }> = {
  Pending:    { bg: HEART.blush,     tx: HEART.redDark,   Icon: Clock        },
  Processing: { bg: HEART.redSoft,   tx: HEART.red,       Icon: Package      },
  Delivered:  { bg: HEART.greenSoft, tx: HEART.green,     Icon: CheckCircle2 },
  Cancelled:  { bg: HEART.divider,   tx: HEART.textMuted, Icon: Ban          },
  Refunded:   { bg: HEART.bg,        tx: HEART.dark,      Icon: RotateCcw    },
};

const statusOf = (o: StoreOrder): SK => {
  if (o.isCancelled) return "Cancelled";
  if (o.isRefunded) return "Refunded";
  const brands = ((o as any).myBrands ?? []) as any[];
  const allFlagsTrue =
    brands.length > 0 &&
    brands.every((b: any) => {
      const ds = b?.deliveryStatus ?? {};
      return ds.sellerDelivered === true && ds.orderDelivered === true && ds.buyerReceived === true;
    });
  if (o.isDelivered || allFlagsTrue) return "Delivered";
  if (o.isPaid) return "Processing";
  return "Pending";
};

const Badge: React.FC<{ s: SK }> = ({ s }) => {
  const c = SCFG[s];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4, background: c.bg,
      border: `2px solid ${HEART.dark}`, borderRadius: 999, padding: "3px 10px",
      boxShadow: `0 3px 0 ${HEART.dark}`, flexShrink: 0,
    }}>
      <c.Icon size={10} color={c.tx} strokeWidth={2.4} />
      <span style={{ fontSize: 10, fontWeight: 800, color: c.tx, fontFamily: "'Sora',sans-serif" }}>{s}</span>
    </div>
  );
};

/* ── photo with a drawn placeholder, no logo stamp ── */
const Photo: React.FC<{ src?: string | null; alt: string }> = ({ src, alt }) => {
  const [dead, setDead] = useState(false);
  if (src && !dead) {
    return (
      <img src={src} alt={alt} loading="lazy" onError={() => setDead(true)}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    );
  }
  return (
    <div style={{
      width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
      background: `repeating-linear-gradient(45deg,${HEART.blush} 0 10px,${HEART.divider} 10px 20px)`,
    }}>
      <Package size={22} color={HEART.textFaint} strokeWidth={1.8} />
    </div>
  );
};

const FilterPill: React.FC<{
  icon: React.ElementType; label: string; value: number; active: boolean; onClick: () => void;
}> = ({ icon: Icon, label, value, active, onClick }) => (
  <button className={`fc${active ? " on" : ""}`} onClick={onClick}>
    <Icon size={12} color={active ? "#fff" : HEART.textMuted} strokeWidth={2.2} />
    {label}
    <span className="fc-n">{value}</span>
  </button>
);

/* ═══════════════════════════════════════════════
   SHIPPING ADDRESS SHEET
═══════════════════════════════════════════════ */
const AddrLine: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({ icon: Icon, label, value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* clipboard blocked */ }
  };
  if (!value) return null;
  return (
    <div className="addr-row">
      <div style={{
        width: 32, height: 32, borderRadius: 10, background: HEART.blush, border: `2px solid ${HEART.dark}`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon size={14} color={HEART.red} strokeWidth={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".14em", color: HEART.textMuted }}>{label.toUpperCase()}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: HEART.dark, marginTop: 2, wordBreak: "break-word" as const }}>{value}</div>
      </div>
      <button className="addr-copy" onClick={handleCopy} aria-label={`Copy ${label}`}>
        {copied ? <Check size={13} color={HEART.green} /> : <Copy size={13} color={HEART.textMuted} />}
      </button>
    </div>
  );
};

const ShippingAddressSheet: React.FC<{ guest: any; guestName: string; onClose: () => void }> = ({ guest, guestName, onClose }) => {
  const addr = guest?.shippingAddress || {};
  const hasAny = addr.streetAddress || addr.apartmentNumber || addr.postalAddress || addr.city || addr.state || addr.country || addr.zipCode;
  const cityLine = [addr.city, addr.state].filter(Boolean).join(", ");
  const countryLine = [addr.zipCode, addr.country].filter(Boolean).join(", ");
  const note = addr.shippingMessage?.trim() || "No delivery instructions";
  const phone = guest?.contactNumber || guest?.phoneNumber || "";

  return (
    <motion.div className="addr-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: .26, ease: [.4, 0, .2, 1] }} onClick={e => e.stopPropagation()} className="addr-sheet">
        <div style={{ height: 4, background: SWEEP, backgroundSize: "300% 100%", animation: "hcSpin 4s linear infinite" }} />
        <div style={{ padding: "18px 20px 24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 10 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 900, color: HEART.dark, fontFamily: "'Sora',sans-serif", letterSpacing: -.4 }}>
                Where it's going
              </div>
              <div style={{ fontSize: 11.5, color: HEART.textMuted, marginTop: 2, fontWeight: 600 }}>{guestName}</div>
            </div>
            <button className="addr-copy" onClick={onClose} aria-label="Close">
              <X size={14} color={HEART.textMuted} />
            </button>
          </div>

          {hasAny ? (
            <div style={{ background: HEART.bg, border: `2.5px solid ${HEART.dark}`, borderRadius: 16, padding: "4px 14px", boxShadow: `0 4px 0 ${HEART.dark}` }}>
              <AddrLine icon={Home}          label="Street address"        value={addr.streetAddress} />
              <AddrLine icon={Building2}     label="Apartment / suite"     value={addr.apartmentNumber} />
              <AddrLine icon={Hash}          label="P.O. box"              value={addr.postalAddress} />
              <AddrLine icon={MapPin}        label="City / region"         value={cityLine} />
              <AddrLine icon={Globe2}        label="Zip / country"         value={countryLine} />
              <AddrLine icon={Phone}         label="Contact number"        value={phone || "Not provided"} />
              <AddrLine icon={MessageSquare} label="Delivery instructions" value={note} />
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "30px 12px", color: HEART.textMuted, fontSize: 13 }}>
              <MapPin size={26} color={HEART.textFaint} style={{ marginBottom: 10 }} />
              <div style={{ fontWeight: 600 }}>This buyer hasn't saved a shipping address.</div>
            </div>
          )}

          <button className="btn bl" style={{ width: "100%", marginTop: 18 }} onClick={onClose}>Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   ITEM TILE
═══════════════════════════════════════════════ */
const ItemTile: React.FC<{
  item: any; listingsMap: Record<string, any>; onNav: (p: string) => void; index: number;
}> = ({ item, listingsMap, onNav, index }) => {
  const p = listingsMap[item.listingId] ?? {};
  const name = p?.name || item.name || "Item";
  /* saleTotal is what was actually charged — recomputing as rate × qty
     overstates perProduct lines, where quantity doesn't move the price. */
  const line = item.saleTotal ?? (item.rate ?? 0) * (item.rateType === "perProduct" ? 1 : (item.qty ?? 1));
  const variants: string[] = item.selectedVariants
    ? Object.values(item.selectedVariants).map((v: any) => String(v))
    : [];
  const go = () => onNav(`/StoreHome/${item.brandId}`);

  return (
    <div className="it" style={{ animation: "hcRise .4s ease both", animationDelay: `${index * 55}ms` }}>
      <div className="it-media" onClick={go} role="link" tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && go()}
        aria-label={`Open the listing for ${name}`}>
        <Photo
          src={
            (item.selectedVariants &&
              Object.values(p?.variants ?? {}).find((v: any) =>
                Object.values(item.selectedVariants).some(
                  (sv: any) => String(sv).toLowerCase() === String(v?.name ?? "").toLowerCase()
                )
              ) as any)?.imageUrl ||
            p?.photos?.[0] || item.photo || item.image
          }
          alt={name}
        />
        <div className="it-dome-tl">×{item.qty ?? 1}</div>
        {variants.length > 0 && <span className="it-pill">{variants.slice(0, 2).join(" · ")}</span>}
        <div className="it-dome-br">€{fmt(line)}</div>
      </div>
      <div className="it-foot">
        <div title={name} style={{
          fontSize: 12.5, fontWeight: 700, color: HEART.dark, fontFamily: "'Sora',sans-serif",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{name}</div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ORDER CARD
═══════════════════════════════════════════════ */
const OrderCard: React.FC<{
  order: StoreOrder; guestName: string; listingsMap: Record<string, any>;
  onNav: (p: string) => void; onDeliver: (o: StoreOrder) => void;
  onCancel: (o: StoreOrder) => void; onRefund: (o: StoreOrder) => void;
  onAddress: (o: StoreOrder) => void; delay: number;
}> = ({ order, guestName, listingsMap, onNav, onDeliver, onCancel, onRefund, onAddress, delay }) => {
  const [open, setOpen] = useState(false);
  const items = order.items || [];
  const fi = items[0];
  const prod = listingsMap[fi?.listingId] ?? {};
  const cnt = items.length;
  const ordId = (order.paymentId || order.id || "").slice(0, 14).toUpperCase();
  const myBrands = ((order as any).myBrands ?? []) as any[];

  /* an order counts as delivered once every brand on it has all three
     delivery flags true — seller shipped it, the system marked it
     delivered, and the buyer confirmed receipt. Don't wait on root
     isDelivered alone, since that can lag the per-brand flags. */
  const allBrandsFullyDelivered =
    myBrands.length > 0 &&
    myBrands.every((b: any) => {
      const ds = b?.deliveryStatus ?? {};
      return ds.sellerDelivered === true && ds.orderDelivered === true && ds.buyerReceived === true;
    });

  const isFullyDelivered = order.isDelivered || allBrandsFullyDelivered;
  const st = statusOf(order);
  const live = !isFullyDelivered && !order.isCancelled && !order.isRefunded;
  const myBrand = myBrands[0];
  const shipped = myBrand?.deliveryStatus?.sellerDelivered === true;

  return (
    <motion.div layout className={`oc${open ? " open" : ""}`} style={{ animationDelay: `${delay * 55}ms` }}>
      <div style={{ height: 4, background: SWEEP, backgroundSize: "300% 100%", animation: "hcSpin 4s linear infinite" }} />

      <div className="oc-head" onClick={() => setOpen(v => !v)} role="button" tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen(v => !v)}>
        <div style={{ width: 104, height: 104, flexShrink: 0, borderRight: `3px solid ${HEART.dark}`, overflow: "hidden" }}>
          <Photo
            src={
              (Object.values(prod?.variants ?? {}).find((v: any) => v?.imageUrl) as any)?.imageUrl ||
              prod?.photos?.[0] || fi?.photo || fi?.image
            }
            alt={prod?.name || fi?.name || "Order"}
          />
        </div>

        <div style={{ flex: 1, padding: "12px 15px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 800, color: HEART.dark, fontFamily: "'Sora',sans-serif",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: -.2,
              }}>
                {prod?.name || fi?.name || "Order"}
                {cnt > 1 && <span style={{ fontSize: 11, color: HEART.textMuted, fontWeight: 600, marginLeft: 6 }}>+{cnt - 1} more</span>}
              </div>
              <div style={{ fontSize: 10, color: HEART.textMuted, marginTop: 4, fontWeight: 600 }}>
                #{ordId} · {fmtD(order.createdAt)} · {cnt} item{cnt > 1 ? "s" : ""}
              </div>
            </div>
            <Badge s={st} />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0,
              fontSize: 11.5, color: HEART.textMuted, fontWeight: 700,
            }}>
              <User size={12} color={HEART.red} strokeWidth={2.2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{guestName}</span>
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 800,
              color: HEART.textMuted, letterSpacing: ".05em", textTransform: "uppercase", flexShrink: 0,
            }}>
              {open ? "Hide" : "Manage"}
              <ChevronRight size={15} color={HEART.red}
                style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .22s" }} />
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: .28, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 18, borderTop: `3px solid ${HEART.dark}`, background: HEART.bg }}>
              <p style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".2em", color: HEART.textMuted, marginBottom: 12 }}>
                WHAT TO PACK
              </p>

              <div className="items-grid" style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 14,
              }}>
                {items.map((item: any, i: number) => (
                  <ItemTile key={(item.listingId || item.id) + i} item={item}
                    listingsMap={listingsMap} onNav={onNav} index={i} />
                ))}
              </div>

              {/* earnings — the host's cut, not the buyer's total */}
              <div style={{
                marginTop: 18, background: HEART.panelBg, border: `2.5px solid ${HEART.dark}`, borderRadius: 18,
                padding: "14px 16px", boxShadow: `0 5px 0 ${HEART.dark}`,
              }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".18em", color: HEART.textMuted, marginBottom: 10 }}>
                  YOUR EARNINGS
                </div>
                {[
                  { l: "Service fee",  v: `€${fmt(order.hostsFee)}` },
                  { l: "Delivery fee", v: order.deliveryFee ? `€${fmt(order.deliveryFee)}` : "Free" },
                ].map(({ l, v }, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${HEART.divider}` }}>
                    <span style={{ fontSize: 12, color: HEART.textMuted }}>{l}</span>
                    <span style={{ fontSize: 12, color: HEART.dark, fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: HEART.dark, fontFamily: "'Sora',sans-serif" }}>You keep</span>
                  <span style={{ fontSize: 19, fontWeight: 900, color: HEART.green, fontFamily: "'Sora',sans-serif", letterSpacing: -.4 }}>
                    €{fmt(order.hostProfit)}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                {live && !shipped && (
                  <button className="btn gr" onClick={() => onDeliver(order)}>
                    <Truck size={12} /> Mark delivered
                  </button>
                )}
                {live && shipped && (
                  <span className="btn gn" style={{ cursor: "default" }}>
                    <CheckCircle2 size={12} /> Shipped — awaiting buyer
                  </span>
                )}
                <button className="btn bl" onClick={() => onAddress(order)}>
                  <MapPin size={12} /> Shipping address
                </button>
                {live && (
                  <button className="btn rd" onClick={() => onCancel(order)}>
                    <Ban size={12} /> Cancel
                  </button>
                )}
                {order.isPaid && !order.isRefunded && (
                  <button className="btn" onClick={() => onRefund(order)}>
                    <RotateCcw size={12} /> Refund
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
interface StoreHostOrdersProps {
  brandId?: string;
}

const StoreHostOrders: React.FC<StoreHostOrdersProps> = ({ brandId }) => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authUser = useSelector((s: RootState) => s.auth.user);

  /* hostProfits is keyed by the SELLER'S userId, never by brandId —
     querying it with a brandId matches nothing. brandId is only used
     below to narrow which lines of each order belong to this store. */
  const hostId = userId ?? authUser?.userId ?? "";

  const orders = useSelector((s: RootState) => (s as any).storeOrders?.hostOrders ?? []);
  const listings = useSelector((s: RootState) => (s as any).storeProducts?.filteredProducts ?? []);
  const allUsers = useSelector((s: RootState) => (s.auth as any).allUsers ?? []);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | SK>("all");
  const [isMob, setIsMob] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [addressOrder, setAddressOrder] = useState<StoreOrder | null>(null);

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!hostId) return;
    const unsub = dispatch(listenToStoreHostOrders(hostId) as any);
    dispatch(listenToPublishedProducts() as any);
    dispatch(filterLocalProductsByCountry(null) as any);
    dispatch(fetchAllUsers());
    return () => { if (typeof unsub === "function") unsub(); };
  }, [hostId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    (listings as any[]).forEach((l: any) => { m[l.listingId] = l; });
    return m;
  }, [listings]);

  const guestName = (id: string) => {
    const u = (allUsers as any[]).find(u => u.userId === id || u.uid === id);
    return u?.firstName || u?.displayName || u?.email || "Guest";
  };
  const findGuest = (id: string) => (allUsers as any[]).find(u => u.userId === id || u.uid === id) || null;

  /* A seller can own several stores, and one order can span them.
     With a brandId we keep only that store's lines and drop orders
     that have none; without one we show everything this host sold. */
  const sorted = useMemo(() => {
    const scoped = brandId
      ? (orders as StoreOrder[])
          .map((o) => ({ ...o, items: (o.items ?? []).filter((i: any) => i.brandId === brandId) }))
          .filter((o) => o.items.length > 0)
      : (orders as StoreOrder[]);

    return [...scoped].sort(
      (a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)
    );
  }, [orders, brandId]);

  const cnt = (s: SK) => sorted.filter(o => statusOf(o) === s).length;
  const pendingN = cnt("Pending");
  const processingN = cnt("Processing");
  const deliveredN = cnt("Delivered");
  const cancelledN = cnt("Cancelled");
  const activeN = pendingN + processingN;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(o => {
      const status = statusOf(o);
      if (filter === "all") {
        if (status === "Delivered" || status === "Cancelled" || status === "Refunded") return false;
      } else if (status !== filter) {
        return false;
      }
      if (!q) return true;
      const fi = o.items?.[0]; const p = listingsMap[fi?.listingId] ?? {};
      return `${p?.name ?? ""} ${o.id ?? ""} ${fi?.name ?? ""} ${guestName(o.guestId)}`.toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, filter, search, listingsMap, allUsers]);

  /* Ships the whole brand — that's the level the backend nests
     deliveryStatus at. Scoped to the current store when StoreCentral
     passes a brandId, else every brand this host owns in the order. */
  const onDeliver = (o: StoreOrder) => {
    const ids = brandId
      ? [brandId]
      : ([...new Set(((o as any).myBrands ?? []).map((b: any) => b.brandId).filter(Boolean))] as string[]);

    if (!ids.length) return;

    dispatch(markStoreItemsDelivered({ orderId: o.id, brandIds: ids, role: "seller" }) as any);
  };
  const onCancel  = (o: StoreOrder) => dispatch(cancelStoreOrder(o.id) as any);
  const onRefund  = (o: StoreOrder) => dispatch(refundStoreOrder(o.id) as any);

  /* ── no orders at all ── */
  if (sorted.length === 0) return (
    <div style={{ minHeight: "100vh", background: HEART.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" }}>
      <style>{CSS}</style>
      <div style={{
        textAlign: "center", maxWidth: 380, margin: "0 20px", background: HEART.panelBg,
        border: `3px solid ${HEART.dark}`, borderRadius: 28, padding: "40px 30px",
        boxShadow: `0 8px 0 ${HEART.dark},0 22px 44px rgba(27,14,6,.16)`,
      }}>
        <div style={{ fontSize: 58, marginBottom: 16, animation: "hcBob 3.2s ease-in-out infinite" }}>📦</div>
        <h2 style={{ fontWeight: 900, fontSize: 26, color: HEART.dark, marginBottom: 8, fontFamily: "'Sora',sans-serif", letterSpacing: -.5 }}>
          No orders yet
        </h2>
        <p style={{ color: HEART.textMuted, fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
          When someone buys from your store, the order lands here with the buyer's address.
        </p>
        <button className="btn gr" style={{ fontSize: 13.5, padding: "13px 28px" }} onClick={() => navigate(`/store/${brandId ?? hostId}/products/new`)}>
          <Plus size={14} /> Add a product <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: HEART.bg, fontFamily: "'DM Sans',sans-serif", paddingBottom: isMob ? 90 : 40 }}>
      <style>{CSS}</style>

      {/* ══ HEADER — title and search share one row ══ */}
      <div style={{
        background: "rgba(251,246,238,.94)", backdropFilter: "blur(14px)",
        borderBottom: `2px solid ${HEART.dark}`, position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ height: 3, background: SWEEP, backgroundSize: "300% 100%", animation: "hcSpin 4s linear infinite" }} />
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "10px 20px 9px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontWeight: 900, fontSize: isMob ? 19 : 22, color: HEART.dark, letterSpacing: -.5, lineHeight: 1.1, fontFamily: "'Sora',sans-serif" }}>
              Store orders
            </h1>
            {activeN > 0 && (
              <p style={{ fontSize: 11, color: HEART.textMuted, fontWeight: 600, marginTop: 1 }}>
                {activeN} waiting to ship
              </p>
            )}
          </div>

          <div className="sw" style={{ marginLeft: "auto", maxWidth: 340 }}>
            <div style={{ padding: "0 11px" }}><Search size={13} color={HEART.textFaint} /></div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search product, buyer, order no."
              style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 12.5, fontWeight: 600, color: HEART.dark, padding: "8px 11px 8px 0" }} />
          </div>
        </div>

        <div style={{ maxWidth: 780, margin: "0 auto", display: "flex", gap: 8, overflowX: "auto", padding: "0 20px 10px", scrollbarWidth: "none" as any }}>
          <FilterPill icon={ShoppingBag}  label="Active"    value={activeN}     active={filter === "all"}        onClick={() => setFilter("all")} />
          <FilterPill icon={Clock}        label="Pending"   value={pendingN}    active={filter === "Pending"}    onClick={() => setFilter("Pending")} />
          <FilterPill icon={Package}      label="Packing"   value={processingN} active={filter === "Processing"} onClick={() => setFilter("Processing")} />
          <FilterPill icon={CheckCircle2} label="Delivered" value={deliveredN}  active={filter === "Delivered"}  onClick={() => setFilter("Delivered")} />
          <FilterPill icon={Ban}          label="Cancelled" value={cancelledN}  active={filter === "Cancelled"}  onClick={() => setFilter("Cancelled")} />
        </div>
      </div>

      {/* ══ ORDER LIST ══ */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: isMob ? "20px 14px" : "26px 20px" }}>
        {filtered.length === 0 ? (
          <div style={{
            textAlign: "center", background: HEART.panelBg, border: `3px solid ${HEART.dark}`, borderRadius: 26,
            padding: "44px 28px", boxShadow: `0 7px 0 ${HEART.dark}`,
          }}>
            <div style={{ fontSize: 46, marginBottom: 14, animation: "hcWiggle 2.6s ease-in-out infinite" }}>🔍</div>
            <h3 style={{ fontWeight: 900, fontSize: 19, color: HEART.dark, marginBottom: 8, fontFamily: "'Sora',sans-serif" }}>No orders match</h3>
            <p style={{ color: HEART.textMuted, fontSize: 13, marginBottom: 22, fontWeight: 600 }}>
              Clear the search or pick another filter above.
            </p>
            <button className="btn gr" onClick={() => { setSearch(""); setFilter("all"); }}>
              <RotateCcw size={12} /> Reset filters
            </button>
          </div>
        ) : filtered.map((order, i) => (
          <OrderCard key={order.id} order={order} guestName={guestName(order.guestId)}
            listingsMap={listingsMap} onNav={path => navigate(path)}
            onDeliver={onDeliver} onCancel={onCancel} onRefund={onRefund}
            onAddress={o => setAddressOrder(o)} delay={i} />
        ))}
      </div>

      <AnimatePresence>
        {addressOrder && (
          <ShippingAddressSheet
            guest={findGuest(addressOrder.guestId)}
            guestName={guestName(addressOrder.guestId)}
            onClose={() => setAddressOrder(null)} />
        )}
      </AnimatePresence>

      {/* ══ MOBILE TAB BAR ══ */}
      {isMob && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: HEART.panelBg,
          display: "flex", justifyContent: "space-around", alignItems: "center",
          borderTop: `2px solid ${HEART.dark}`, zIndex: 100, boxShadow: "0 -6px 24px rgba(27,14,6,.10)",
        }}>
          {[
            { icon: <Home size={21} />,        label: "Home",     path: `/StoreProductsHome/${brandId ?? hostId}`, active: false },
            { icon: <Tag size={21} />,         label: "Products", path: `/store/${brandId ?? hostId}/products`,    active: false },
            { icon: <ShoppingBag size={21} />, label: "Orders",   path: `/store/${brandId ?? hostId}/orders`,      active: true  },
            { icon: <User size={21} />,        label: "Profile",  path: `/profile/${hostId}`,                      active: false },
          ].map(t => (
            <motion.button key={t.label} whileTap={{ scale: .88 }} className={`tab${t.active ? " on" : ""}`} onClick={() => navigate(t.path)}>
              {React.cloneElement(t.icon as any, { color: t.active ? HEART.red : HEART.textFaint, strokeWidth: t.active ? 2.4 : 1.9 })}
              <span>{t.label}</span>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoreHostOrders;